import type {
  PeriodType,
  TableColumn,
  TableRow,
} from "@/apis/ixd-editor/types";
import {
  expandSpanGrid,
  type SpanGrid,
  type SpanGridSlot,
  spanGridKey,
} from "@/utils/expand-span-grid";
import {
  buildCellIdFromTableColumn,
  buildCellIdLookupCandidates,
  normalizeCellId,
  parseCellId,
} from "./cell-index";

type HeaderScope = "row" | "col";

type GridCell = SpanGridSlot<TableColumn>;

type ExpandedGrid = SpanGrid<TableColumn>;

export interface ValueCellSemanticContext {
  lineItemHeader: TableColumn | null;
  rowLabel: string;
  axisLabels: string[];
  negated: boolean;
  decimalDigits: number | null;
  balance: "DEBIT" | "CREDIT" | null;
}

export type TableSemanticResolver = (params: {
  rowIndex: number;
  colIndex: number;
  column: TableColumn;
}) => ValueCellSemanticContext;

const isHeaderWithScope = (
  column: TableColumn | undefined,
  scope: HeaderScope,
): column is TableColumn => column?.tagName === "th" && column.scope === scope;

const getIdParts = (id: string | null | undefined): string[] => {
  if (!id) return [];
  const parts = id.split(":");
  const axisParts = parts[2]?.split("|").filter(Boolean) ?? [];
  return [id, normalizeCellId(id), parts[1], ...axisParts].filter(
    (part): part is string => !!part,
  );
};

const getColumnLookupKeys = (column: TableColumn): Set<string> =>
  new Set([
    ...getIdParts(column.id),
    ...getIdParts(column.attributes.contextKey),
  ]);

/**
 * th 헤더가 가리키는 element id 후보들(라인아이템·축 멤버 + 원본/정규 cellId).
 * 모두 공백이 없는 토큰이라, DSD 헤더 태그(lineItem/member)로 대응 IXD 헤더를
 * 찾을 때 `data-header-ids` 속성(공백 구분)으로 노출해 `~=` 선택자로 매칭한다.
 */
export const getHeaderLookupKeys = (column: TableColumn): string[] => [
  ...getColumnLookupKeys(column),
];

const getLineItemIdFromCandidates = (candidates: string[]): string | null => {
  for (const candidate of candidates) {
    const lineItemId = candidate.split(":")[1];
    if (lineItemId) return lineItemId;
  }
  return null;
};

const getAxisIdsFromCandidates = (candidates: string[]): string[] => {
  for (const candidate of candidates) {
    const axisPart = candidate.split(":")[2];
    if (!axisPart) continue;
    const axisIds = axisPart.split("|").filter(Boolean);
    if (axisIds.length > 0) return axisIds;
  }
  return [];
};

/**
 * 3개월/누적 헤더도 id 조각으로 들어간다. 먼저 넣은 값을 지키므로, 같은 id 의 진짜 헤더가
 * 3개월/누적 헤더보다 위 줄에 있어야 라벨이 "3개월"/"누적"으로 덮이지 않는다 — 행 순서에
 * 기댄다. 다른 화면도 이 맵을 쓰므로 3개월/누적 헤더를 빼는 것은 따로 다룬다.
 */
export const buildTableLabelMap = (rows: TableRow[]): Map<string, string> => {
  const map = new Map<string, string>();
  const addLabel = (key: string | null | undefined, text: string) => {
    if (!key || !text) return;
    for (const part of getIdParts(key)) {
      if (!map.has(part)) map.set(part, text);
    }
  };

  for (const row of rows) {
    for (const column of row.columns) {
      if (column.tagName !== "th") continue;
      addLabel(column.id, column.text);
      addLabel(column.attributes.contextKey, column.text);
    }
  }
  return map;
};

const buildExpandedGrid = (rows: TableRow[]): ExpandedGrid =>
  expandSpanGrid(
    rows.map((row) => row.columns),
    (column) => column,
  );

/**
 * 3개월/누적 헤더. id 가 바로 위 헤더(비전치: 축 멤버, 전치: 라인아이템) id 에 누적 구분을
 * 붙인 것이라 그 id 로도 걸리지만, 라인아이템도 멤버도 아니다 — 값 칸 바로 위에 있어
 * 먼저 잡히므로 건너뛴다.
 */
const isAccumulatedTypeHeader = (column: TableColumn): boolean =>
  parseCellId(column.id).accumulatedType !== "";

const findHeaderBySemanticId = (
  rows: TableRow[],
  semanticId: string | null,
): TableColumn | null => {
  if (!semanticId) return null;
  for (const row of rows) {
    for (const column of row.columns) {
      if (column.tagName !== "th" || isAccumulatedTypeHeader(column)) continue;
      if (getColumnLookupKeys(column).has(semanticId)) return column;
    }
  }
  return null;
};

const findNearestHeaderLeft = (
  grid: GridCell[][],
  visualRowIndex: number,
  visualColIndex: number,
  options?: { scope?: HeaderScope; semanticId?: string | null },
): TableColumn | null => {
  const row = grid[visualRowIndex] ?? [];
  for (let colIndex = visualColIndex - 1; colIndex >= 0; colIndex -= 1) {
    const column = row[colIndex]?.item;
    if (!isMatchingHeader(column, options)) continue;
    return column;
  }
  return null;
};

const findNearestHeaderAbove = (
  grid: GridCell[][],
  visualRowIndex: number,
  visualColIndex: number,
  options?: { scope?: HeaderScope; semanticId?: string | null },
): TableColumn | null => {
  for (let rowIndex = visualRowIndex - 1; rowIndex >= 0; rowIndex -= 1) {
    const column = grid[rowIndex]?.[visualColIndex]?.item;
    if (!isMatchingHeader(column, options)) continue;
    return column;
  }
  return null;
};

const isMatchingHeader = (
  column: TableColumn | undefined,
  options?: { scope?: HeaderScope; semanticId?: string | null },
): column is TableColumn => {
  if (column?.tagName !== "th" || !column.text) return false;
  if (isAccumulatedTypeHeader(column)) return false;
  if (options?.scope && !isHeaderWithScope(column, options.scope)) return false;
  if (
    options?.semanticId &&
    !getColumnLookupKeys(column).has(options.semanticId)
  ) {
    return false;
  }
  return true;
};

const getCellIdCandidates = (
  periodType: PeriodType,
  column: TableColumn,
): string[] => {
  const canonicalCellId = buildCellIdFromTableColumn(periodType, column);
  return buildCellIdLookupCandidates(
    periodType,
    canonicalCellId,
    column.id,
    column.attributes.contextKey,
  );
};

export const getReferenceBalance = (
  rows: TableRow[],
): "DEBIT" | "CREDIT" | null => {
  for (const row of rows) {
    for (const column of row.columns) {
      if (column.tagName === "th" && column.balance) return column.balance;
    }
  }
  return null;
};

export const createTableSemanticResolver = ({
  rows,
  periodType,
  isTransposed,
  labelMap = buildTableLabelMap(rows),
}: {
  rows: TableRow[];
  periodType: PeriodType;
  isTransposed: boolean;
  labelMap?: Map<string, string>;
}): TableSemanticResolver => {
  const { grid, originIndex } = buildExpandedGrid(rows);
  const cellIdCandidatesByColumn = new WeakMap<TableColumn, string[]>();

  const getCachedCellIdCandidates = (column: TableColumn): string[] => {
    const cached = cellIdCandidatesByColumn.get(column);
    if (cached) return cached;
    const candidates = getCellIdCandidates(periodType, column);
    cellIdCandidatesByColumn.set(column, candidates);
    return candidates;
  };

  return ({ rowIndex, colIndex, column }) => {
    const candidates = getCachedCellIdCandidates(column);
    const lineItemId = getLineItemIdFromCandidates(candidates);
    const axisIds = getAxisIdsFromCandidates(candidates);
    const visualPosition =
      originIndex.get(spanGridKey(rowIndex, colIndex)) ?? null;

    const positionalLineItemHeader = visualPosition
      ? isTransposed
        ? (findNearestHeaderAbove(
            grid,
            visualPosition.row,
            visualPosition.col,
            {
              semanticId: lineItemId,
            },
          ) ??
          findNearestHeaderAbove(grid, visualPosition.row, visualPosition.col, {
            scope: "col",
          }))
        : (findNearestHeaderLeft(grid, visualPosition.row, visualPosition.col, {
            semanticId: lineItemId,
          }) ??
          findNearestHeaderLeft(grid, visualPosition.row, visualPosition.col, {
            scope: "row",
          }))
      : null;
    const lineItemHeader =
      positionalLineItemHeader ?? findHeaderBySemanticId(rows, lineItemId);

    const positionalAxisHeader = visualPosition
      ? isTransposed
        ? findNearestHeaderLeft(grid, visualPosition.row, visualPosition.col, {
            scope: "row",
          })
        : findNearestHeaderAbove(grid, visualPosition.row, visualPosition.col, {
            scope: "col",
          })
      : null;

    const axisLabels = axisIds.map((axisId) => {
      const positionalAxisLabel = visualPosition
        ? isTransposed
          ? findNearestHeaderLeft(
              grid,
              visualPosition.row,
              visualPosition.col,
              {
                semanticId: axisId,
              },
            )?.text
          : findNearestHeaderAbove(
              grid,
              visualPosition.row,
              visualPosition.col,
              { semanticId: axisId },
            )?.text
        : null;
      return positionalAxisLabel ?? labelMap.get(axisId) ?? axisId;
    });
    if (axisLabels.length === 0 && positionalAxisHeader?.text) {
      axisLabels.push(positionalAxisHeader.text);
    }

    return {
      lineItemHeader,
      rowLabel:
        lineItemHeader?.text ??
        (lineItemId ? labelMap.get(lineItemId) : undefined) ??
        "",
      axisLabels,
      negated: lineItemHeader?.negated ?? false,
      decimalDigits: lineItemHeader?.decimalDigits ?? null,
      balance: lineItemHeader?.balance ?? null,
    };
  };
};
